/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BiSearch, BiEdit, BiTrash } from 'react-icons/bi';
import { getShowroomDealer, addShowroomDealer, updateShowroomDealer, removeShowroomDealer } from '../slice/showroomDealerSlice';
import { getShowroomCategory } from '../slice/showroomCategorySlice';
import './DealerStore.css';
import { toast } from 'react-toastify';
import ConfirmModal from '../common/ConfirmModal';

const DealerStore = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: dealers = [], loading } = useSelector((state) => state.showroomDealer);
  const { data: categories = [] } = useSelector((state) => state.showroomCategory);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter]);

  // Custom Dropdown Open/Close states
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);

  // Custom Dropdown Search states
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [stateSearchTerm, setStateSearchTerm] = useState('');

  // Selected filters
  const [selectedFilterCountry, setSelectedFilterCountry] = useState(null);
  const [selectedFilterState, setSelectedFilterState] = useState(null);

  // Custom Confirmation Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDealerToEdit, setPendingDealerToEdit] = useState(null);
  const [deleteDealerTarget, setDeleteDealerTarget] = useState(null);

  // Countries and States local data lists
  const [countriesList, setCountriesList] = useState([]);
  const [statesList, setStatesList] = useState([]);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    id: null,
    showroom: '',
    category_id: '',
    sub_category_id: '',
    email_contact: '',
    phone_contact: '',
    seo_url: '',
    address: '',
    location: '',
    pincode: '',
    state_id: '',
    country_id: '101' // Default to India (101)
  });

  useEffect(() => {
    dispatch(getShowroomDealer());
    dispatch(getShowroomCategory());

    // Fetch Countries and States
    const apiBase = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');
    fetch(`${apiBase}/showroom-dealers/countries`)
      .then((res) => res.json())
      .then((data) => setCountriesList(data))
      .catch((err) => console.error('Error fetching countries:', err));

    fetch(`${apiBase}/showroom-dealers/states`)
      .then((res) => res.json())
      .then((data) => setStatesList(data))
      .catch((err) => console.error('Error fetching states:', err));
  }, [dispatch]);

  // Click outside to close custom select menus
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.custom-select-container')) {
        setCategoryDropdownOpen(false);
        setCountryDropdownOpen(false);
        setStateDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.showroom || !formData.category_id) {
      toast.error('Please fill all required fields');
      return;
    }

    if (editMode) {
      dispatch(updateShowroomDealer({ id: formData.id, data: formData }));
    } else {
      dispatch(addShowroomDealer(formData));
    }
    setShowModal(false);
    setEditMode(false);
    setFormData({
      id: null,
      showroom: '',
      category_id: '',
      sub_category_id: '',
      email_contact: '',
      phone_contact: '',
      seo_url: '',
      address: '',
      location: '',
      pincode: '',
      state_id: '',
      country_id: '101'
    });
  };

  const handleStatusToggle = (dealer) => {
    const newStatus = dealer.status === 1 ? 0 : 1;
    dispatch(updateShowroomDealer({ id: dealer.id, data: { status: newStatus } }));
  };

  const handleDelete = (dealer) => {
    setDeleteDealerTarget(dealer);
  };

  const handleConfirmDelete = () => {
    if (deleteDealerTarget) {
      dispatch(removeShowroomDealer(deleteDealerTarget.id));
      setDeleteDealerTarget(null);
    }
  };

  const initiateEdit = (dealer) => {
    console.log('initiateEdit called with:', dealer);
    setPendingDealerToEdit(dealer);
    setShowConfirmModal(true);
  };

  const handleConfirmEdit = () => {
    console.log('handleConfirmEdit triggered, pending:', pendingDealerToEdit);
    if (pendingDealerToEdit) {
      setShowConfirmModal(false);
      navigate(`/edit-dealer-store/${pendingDealerToEdit.id}`);
    }
    setPendingDealerToEdit(null);
  };

  const filteredDealers = (Array.isArray(dealers) ? dealers : []).filter((dealer) => {
    if (searchTerm && !dealer.showroom?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (categoryFilter !== 'All') {
      const matchText = `${dealer.showroom} ${dealer.main_category_name} ${dealer.sub_category_name}`.toLowerCase();
      if (!matchText.includes(categoryFilter.toLowerCase())) return false;
    }
    if (selectedFilterCountry && dealer.country_id != selectedFilterCountry.id) return false;
    if (selectedFilterState && dealer.state_id != selectedFilterState.id) return false;
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDealers = filteredDealers.slice(indexOfFirstItem, indexOfLastItem);

  const mainCategoriesList = (Array.isArray(categories) ? categories : []).filter((c) => c.category_type === 0);
  const subCategoriesList = (Array.isArray(categories) ? categories : []).filter(
    (c) => c.category_type !== 0 && c.parent_category_name === mainCategoriesList.find((m) => m.id == formData.category_id)?.category
  );

  // Filter states list based on selected country
  const filteredStatesList = statesList.filter((s) => s.country_id == formData.country_id);

  // Helper to generate Emoji Flag from Country ISO code
  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  const formatDate = (dateString) => {
    if (!dateString) return { dateOnly: '-', timeOnly: '' };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { dateOnly: dateString, timeOnly: '' };
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const strHours = String(hours).padStart(2, '0');

      return {
        dateOnly: `${day}/${month}/${year}`,
        timeOnly: `${strHours}:${minutes} ${ampm}`
      };
    } catch {
      return { dateOnly: '-', timeOnly: '' };
    }
  };

  return (
    <div className="premium-page-container">
      <div className="premium-page-header">
        <h1 className="page-title">Manage Dealer Store</h1>
        <button
          className="btn-premium"
          onClick={() => {
            setEditMode(false);
            setFormData({
              id: null,
              showroom: '',
              category_id: '',
              sub_category_id: '',
              email_contact: '',
              phone_contact: '',
              seo_url: '',
              address: '',
              location: '',
              pincode: '',
              state_id: '',
              country_id: '101'
            });
            setShowModal(true);
          }}
        >
          + Create
        </button>
      </div>

      <div className="premium-controls-bar">
        <div className="filters-row-single-line">
          {/* Custom Category Dropdown */}
          <div className="custom-select-container">
            <button
              type="button"
              className={`custom-select-trigger ${categoryDropdownOpen ? 'active' : ''}`}
              onClick={() => {
                setCategoryDropdownOpen(!categoryDropdownOpen);
                setCountryDropdownOpen(false);
                setStateDropdownOpen(false);
              }}
            >
              <span>{categoryFilter === 'All' ? '📁 All Category' : `📁 ${categoryFilter}`}</span>
              <span style={{ fontSize: '10px', color: '#c4a882' }}>{categoryDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {categoryDropdownOpen && (
              <div className="custom-select-menu" style={{ width: '200px' }}>
                <ul className="custom-select-options">
                  {['All', 'Atelier', 'Advantage', 'Select'].map((cat) => (
                    <li
                      key={cat}
                      className={`custom-select-option ${categoryFilter === cat ? 'selected' : ''}`}
                      onClick={() => {
                        setCategoryFilter(cat);
                        setCategoryDropdownOpen(false);
                      }}
                    >
                      {cat === 'All' ? '📁 All Category' : `📁 ${cat}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Custom Country Dropdown */}
          <div className="custom-select-container">
            <button
              type="button"
              className={`custom-select-trigger ${countryDropdownOpen ? 'active' : ''}`}
              onClick={() => {
                setCountryDropdownOpen(!countryDropdownOpen);
                setCategoryDropdownOpen(false);
                setStateDropdownOpen(false);
              }}
            >
              <span>
                {selectedFilterCountry
                  ? `${getFlagEmoji(selectedFilterCountry.iso2)} ${selectedFilterCountry.name}`
                  : '🌐 List all Countries...'}
              </span>
              <span style={{ fontSize: '10px', color: '#c4a882' }}>{countryDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {countryDropdownOpen && (
              <div className="custom-select-menu">
                <div className="custom-select-search-box">
                  <BiSearch className="custom-select-search-icon" />
                  <input
                    type="text"
                    placeholder="Search Country"
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
                <ul className="custom-select-options">
                  <li
                    className={`custom-select-option ${!selectedFilterCountry ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedFilterCountry(null);
                      setSelectedFilterState(null);
                      setCountryDropdownOpen(false);
                      setCountrySearchTerm('');
                    }}
                  >
                    🌐 List all Countries
                  </li>
                  {countriesList
                    .filter((c) => c.name.toLowerCase().includes(countrySearchTerm.toLowerCase()))
                    .map((c) => (
                      <li
                        key={c.id}
                        className={`custom-select-option ${selectedFilterCountry?.id === c.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedFilterCountry(c);
                          setSelectedFilterState(null); // Reset state when country changes
                          setCountryDropdownOpen(false);
                          setCountrySearchTerm('');
                        }}
                      >
                        <span>{getFlagEmoji(c.iso2)}</span>
                        <span>{c.name}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          {/* Custom State Dropdown */}
          <div className="custom-select-container">
            <button
              type="button"
              className={`custom-select-trigger ${stateDropdownOpen ? 'active' : ''}`}
              onClick={() => {
                if (!selectedFilterCountry) {
                  toast.info('Please select a country first');
                  return;
                }
                setStateDropdownOpen(!stateDropdownOpen);
                setCategoryDropdownOpen(false);
                setCountryDropdownOpen(false);
              }}
              disabled={!selectedFilterCountry}
              style={{ opacity: selectedFilterCountry ? 1 : 0.6 }}
            >
              <span>{selectedFilterState ? `🔍 ${selectedFilterState.name}` : '🔍 List all States...'}</span>
              <span style={{ fontSize: '10px', color: '#c4a882' }}>{stateDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {stateDropdownOpen && selectedFilterCountry && (
              <div className="custom-select-menu">
                <div className="custom-select-search-box">
                  <BiSearch className="custom-select-search-icon" />
                  <input
                    type="text"
                    placeholder="Search State"
                    value={stateSearchTerm}
                    onChange={(e) => setStateSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
                <ul className="custom-select-options">
                  <li
                    className={`custom-select-option ${!selectedFilterState ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedFilterState(null);
                      setStateDropdownOpen(false);
                      setStateSearchTerm('');
                    }}
                  >
                    🔍 List all States
                  </li>
                  {statesList
                    .filter(
                      (s) => s.country_id === selectedFilterCountry.id && s.name.toLowerCase().includes(stateSearchTerm.toLowerCase())
                    )
                    .map((s) => (
                      <li
                        key={s.id}
                        className={`custom-select-option ${selectedFilterState?.id === s.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedFilterState(s);
                          setStateDropdownOpen(false);
                          setStateSearchTerm('');
                        }}
                      >
                        {s.name}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          {/* Single line Search Box */}
          <div className="showroom-search search-box" style={{ margin: 0, minWidth: '220px', flex: 1, maxWidth: '300px' }}>
            <BiSearch className="search-icon" size={18} />
            <input
              type="text"
              className="search-input"
              placeholder="Search dealers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="premium-table-wrapper animate-fade-up">
        <table className="premium-table">
          <thead>
            <tr>
              <th style={{ width: '3%', textAlign: 'center' }}>
                <input type="checkbox" className="premium-checkbox" />
              </th>
              <th style={{ width: '8%' }}>Status</th>
              <th style={{ width: '16%' }}>Showroom Name</th>
              <th style={{ width: '11%' }}>Category</th>
              <th style={{ width: '13%' }}>Contacts</th>
              <th style={{ width: '15%' }}>Address</th>
              <th style={{ width: '11%' }}>State & Country</th>
              <th style={{ width: '13%' }}>Created On</th>
              <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentDealers.length > 0 ? (
              currentDealers.map((dealer) => {
                const formattedDate = formatDate(dealer.created_on);
                return (
                  <tr key={dealer.id}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" className="premium-checkbox" />
                    </td>
                    <td>
                      <div className="switch-container" style={{ gap: '4px' }}>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          htmlFor={`status-toggle-${dealer.id}`}
                          className="switch"
                          style={{ margin: 0, width: '32px', height: '17px', minWidth: '32px' }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              width: '1px',
                              height: '1px',
                              padding: 0,
                              margin: '-1px',
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              border: 0
                            }}
                          >
                            Toggle Status
                          </span>
                          <input
                            id={`status-toggle-${dealer.id}`}
                            type="checkbox"
                            checked={dealer.status === 1}
                            onChange={() => handleStatusToggle(dealer)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="switch-label" style={{ fontSize: '10.5px' }}>
                          {dealer.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div
                        className="text-truncate-cell"
                        style={{ maxWidth: '130px', fontWeight: '600', color: '#f5f0e8', fontSize: '12px' }}
                        title={dealer.showroom}
                      >
                        {dealer.showroom}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                        {dealer.main_category_name && (
                          <span
                            style={{
                              color: '#9e9689',
                              fontSize: '9.5px',
                              fontWeight: 600,
                              letterSpacing: '0.4px',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {dealer.main_category_name}
                          </span>
                        )}
                        {dealer.sub_category_name ? (
                          <span className="badge-type sub" style={{ fontSize: '9.5px', padding: '2px 5px' }}>
                            {dealer.sub_category_name}
                          </span>
                        ) : (
                          <span style={{ color: '#666', fontSize: '10.5px' }}>-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ lineHeight: '1.3', fontSize: '11.5px', maxWidth: '110px' }}>
                        {dealer.email_contact && (
                          <div
                            className="text-truncate-cell"
                            style={{ color: '#f5f0e8', fontWeight: 500, maxWidth: '110px' }}
                            title={dealer.email_contact}
                          >
                            {dealer.email_contact}
                          </div>
                        )}
                        {dealer.phone_contact && (
                          <div style={{ color: '#c4a882', fontWeight: 600, fontSize: '11px', marginTop: '1px' }}>
                            {dealer.phone_contact}
                          </div>
                        )}
                        {!dealer.email_contact && !dealer.phone_contact && '-'}
                      </div>
                    </td>
                    <td>
                      <div
                        className="text-truncate-cell"
                        style={{ maxWidth: '120px' }}
                        title={`${dealer.address || ''}${dealer.pincode ? ' - ' + dealer.pincode : ''}`}
                      >
                        <span>{dealer.address || '-'}</span>
                        {dealer.pincode && <span style={{ color: '#c4a882', fontWeight: 600, marginLeft: '3px' }}>({dealer.pincode})</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ lineHeight: '1.25' }}>
                        <div style={{ fontWeight: '600', color: '#ffffff', fontSize: '11.5px' }}>{dealer.state_name || '-'}</div>
                        <div style={{ color: '#8a8377', fontSize: '10.5px', marginTop: '1px' }}>{dealer.country_name || '-'}</div>
                      </div>
                    </td>
                    <td className="nowrap-cell">
                      <div style={{ color: '#f5f0e8', fontWeight: 600, fontSize: '11.5px', lineHeight: '1.25' }}>
                        {formattedDate.dateOnly}
                      </div>
                      {formattedDate.timeOnly && (
                        <div style={{ color: '#888', fontSize: '10px', marginTop: '1px' }}>{formattedDate.timeOnly}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="inline-actions-container" style={{ justifyContent: 'center', gap: '4px' }}>
                        <button className="btn-inline-action edit-action" title="Edit" onClick={() => initiateEdit(dealer)}>
                          <BiEdit size={13} />
                        </button>
                        <button className="btn-inline-action delete-action" title="Delete" onClick={() => handleDelete(dealer)}>
                          <BiTrash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                  {loading ? 'Loading...' : 'No dealers found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredDealers.length > 0 && (
        <div className="premium-pagination-container" style={{ margin: '1.5rem 0' }}>
          <div className="premium-pagination-info">
            Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredDealers.length)}</span> of{' '}
            <span>{filteredDealers.length}</span> entries
          </div>
          <div className="premium-pagination-list">
            <button className="premium-pagination-btn arrow-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              &laquo;
            </button>
            <button
              className="premium-pagination-btn arrow-btn"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              &lsaquo;
            </button>

            {Array.from({ length: Math.ceil(filteredDealers.length / itemsPerPage) }, (_, idx) => idx + 1)
              .filter((page) => {
                const totalPages = Math.ceil(filteredDealers.length / itemsPerPage);
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => {
                return (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                    )}
                    <button
                      className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}

            <button
              className="premium-pagination-btn arrow-btn"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredDealers.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(filteredDealers.length / itemsPerPage)}
            >
              &rsaquo;
            </button>
            <button
              className="premium-pagination-btn arrow-btn"
              onClick={() => setCurrentPage(Math.ceil(filteredDealers.length / itemsPerPage))}
              disabled={currentPage === Math.ceil(filteredDealers.length / itemsPerPage)}
            >
              &raquo;
            </button>
          </div>
        </div>
      )}

      {/* Modal for Create/Edit Dealer */}
      {showModal && (
        <div className="premium-modal-overlay">
          <div className="dealer-modal-container">
            <div className="dealer-modal-header">
              <div>
                <div className="dealer-modal-eyebrow">SHOWROOM NETWORK</div>
                <h3 className="dealer-modal-title">{editMode ? 'Edit Dealer Store' : 'Create Dealer Store'}</h3>
              </div>
              <button
                type="button"
                className="dealer-modal-close-btn"
                onClick={() => {
                  setShowModal(false);
                  setEditMode(false);
                }}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            <div className="dealer-modal-body">
              <form onSubmit={handleSubmit} className="dealer-modal-form">
                {/* Category Row */}
                <div className="dealer-form-row-2">
                  <div className="dealer-form-group">
                    <label htmlFor="main_category_select">
                      Main Category <span className="dealer-req-star">*</span>
                    </label>
                    <div className="dealer-select-wrapper">
                      <select
                        id="main_category_select"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                        className="dealer-form-control"
                      >
                        <option value="">Select Category</option>
                        {mainCategoriesList.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="dealer-form-group">
                    <label htmlFor="sub_category_select">Sub Category</label>
                    <div className="dealer-select-wrapper">
                      <select
                        id="sub_category_select"
                        name="sub_category_id"
                        value={formData.sub_category_id}
                        onChange={handleInputChange}
                        className="dealer-form-control"
                      >
                        <option value="">Select Sub Category</option>
                        {subCategoriesList.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Showroom Name */}
                <div className="dealer-form-group">
                  <label htmlFor="showroom_input">
                    Showroom Name <span className="dealer-req-star">*</span>
                  </label>
                  <input
                    id="showroom_input"
                    type="text"
                    name="showroom"
                    value={formData.showroom}
                    onChange={handleInputChange}
                    placeholder="e.g. Aristo Experience Center - Indiranagar"
                    required
                    className="dealer-form-control"
                  />
                </div>

                {/* Contact Row */}
                <div className="dealer-form-row-2">
                  <div className="dealer-form-group">
                    <label htmlFor="email_input">
                      Email Contact
                    </label>
                    <input
                      id="email_input"
                      type="email"
                      name="email_contact"
                      value={formData.email_contact}
                      onChange={handleInputChange}
                      placeholder="showroom@aristo-india.com"
                      className="dealer-form-control"
                    />
                  </div>

                  <div className="dealer-form-group">
                    <label htmlFor="phone_input">Phone Contact</label>
                    <input
                      id="phone_input"
                      type="text"
                      name="phone_contact"
                      value={formData.phone_contact}
                      onChange={handleInputChange}
                      placeholder="+91 98765 43210"
                      className="dealer-form-control"
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="dealer-form-group">
                  <label htmlFor="address_textarea">Full Address</label>
                  <textarea
                    id="address_textarea"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter complete address including building, street, area details..."
                    rows="3"
                    className="dealer-form-control dealer-textarea"
                  />
                </div>

                {/* City & Pincode */}
                <div className="dealer-form-row-2">
                  <div className="dealer-form-group">
                    <label htmlFor="location_input">City / Location</label>
                    <input
                      id="location_input"
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g. Bangalore"
                      className="dealer-form-control"
                    />
                  </div>
                  <div className="dealer-form-group">
                    <label htmlFor="pincode_input">Pincode</label>
                    <input
                      id="pincode_input"
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      placeholder="e.g. 560038"
                      className="dealer-form-control"
                    />
                  </div>
                </div>

                {/* Country & State */}
                <div className="dealer-form-row-2">
                  <div className="dealer-form-group">
                    <label htmlFor="country_select">Country</label>
                    <div className="dealer-select-wrapper">
                      <select
                        id="country_select"
                        name="country_id"
                        value={formData.country_id}
                        onChange={handleInputChange}
                        className="dealer-form-control"
                      >
                        <option value="">Select Country</option>
                        {countriesList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="dealer-form-group">
                    <label htmlFor="state_select">State</label>
                    <div className="dealer-select-wrapper">
                      <select
                        id="state_select"
                        name="state_id"
                        value={formData.state_id}
                        onChange={handleInputChange}
                        className="dealer-form-control"
                      >
                        <option value="">Select State</option>
                        {filteredStatesList.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* SEO URL */}
                <div className="dealer-form-group">
                  <label htmlFor="seo_input">SEO URL</label>
                  <input
                    id="seo_input"
                    type="text"
                    name="seo_url"
                    value={formData.seo_url}
                    onChange={handleInputChange}
                    placeholder="e.g. aristo-experience-center-bangalore"
                    className="dealer-form-control"
                  />
                </div>

                {/* Footer Buttons */}
                <div className="dealer-modal-footer">
                  <button
                    type="button"
                    className="dealer-btn-cancel"
                    onClick={() => {
                      setShowModal(false);
                      setEditMode(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="dealer-btn-submit">
                    {editMode ? 'Update Dealer Store' : 'Create Dealer Store'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Edit Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal && !!pendingDealerToEdit}
        type="edit"
        title="Modify Showroom"
        itemName={pendingDealerToEdit?.showroom}
        itemType="showroom"
        onConfirm={handleConfirmEdit}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingDealerToEdit(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteDealerTarget}
        type="delete"
        title="Delete Showroom"
        itemName={deleteDealerTarget?.showroom}
        itemType="showroom"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDealerTarget(null)}
      />
    </div>
  );
};

export default DealerStore;
